import React, { useEffect, useState } from "react";
import { UserPlus, ShieldCheck, ShieldOff, KeyRound, RefreshCcw, Ban, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { fmtDay, formatErr } from "../../lib/api";
import { Loading, PageHeader, StatusPill, ConfirmDelete } from "../../components/Shared";
import { useAuth } from "../../context/AuthContext";

const ROLE_LABELS = { super_admin: "Founder / Super Admin", admin: "Admin", ops_manager: "Ops Manager", ops_executive: "Ops Executive", care_manager: "Care Manager" };
const INVITABLE = ["ops_executive", "ops_manager", "care_manager", "admin"];

export default function Staff() {
  const { user } = useAuth();
  const [staff, setStaff] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "ops_executive" });
  const [showForm, setShowForm] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const allowed = ["admin", "super_admin"].includes(user?.role);
  const isSuper = user?.role === "super_admin";

  const load = () => api.get("/admin/staff").then((r) => setStaff(r.data));
  useEffect(() => { if (allowed) load().catch((e) => { toast.error(formatErr(e)); setStaff([]); }); }, [allowed]);

  if (!allowed) {
    return <div className="p1-card p-10 text-center text-sm text-stone-500" data-testid="staff-denied">Staff management is restricted to the Founder / Admin roles.</div>;
  }
  if (!staff) return <Loading label="Loading staff…" />;

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/admin/staff", form);
      toast.success(r.data.message);
      setShowForm(false); setForm({ name: "", email: "", phone: "", role: "ops_executive" }); load();
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };
  const patch = async (id, body, msg) => {
    try { await api.patch(`/admin/staff/${id}`, body); toast.success(msg); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const revoke = async (id) => {
    try { const r = await api.post(`/admin/staff/${id}/revoke-sessions`); toast.success(r.data.message); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const mfaReset = async (id) => {
    try { const r = await api.post(`/admin/staff/${id}/mfa-reset`); toast.success(r.data.message); load(); }
    catch (err) { toast.error(formatErr(err)); }
  };
  const changePw = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/change-password", pw);
      toast.success("Password updated. Other sessions have been signed out.");
      setPw({ current_password: "", new_password: "" });
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const deleteStaff = async () => {
    setBusy(true);
    try {
      const r = await api.delete(`/admin/staff/${delTarget.id}`);
      toast.success(`Account deleted.${r.data.assignments_cleared ? ` ${r.data.assignments_cleared} assignment(s) cleared.` : ""}`);
      setDelTarget(null); load();
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const roleOptions = (s) => INVITABLE.filter((r) => isSuper || r !== "admin");

  return (
    <div className="max-w-full overflow-x-hidden" data-testid="admin-staff">
      <PageHeader title="Staff Access" sub="Only Founder/Admin can create staff. Every change is audited; disabling or role changes revoke sessions instantly."
        actions={<button onClick={() => setShowForm(!showForm)} className="p1-btn-primary !px-5 !py-2.5" data-testid="invite-staff-btn"><UserPlus className="h-4 w-4" /> Invite Staff</button>} />

      <div className="mb-6 grid gap-6 lg:grid-cols-3">
        <form onSubmit={changePw} className="p1-card p-5" data-testid="change-password-card">
          <p className="flex items-center gap-2 font-heading font-semibold text-stone-900"><KeyRound className="h-4 w-4 text-emerald-800" /> My account — change password</p>
          <input type="password" className="p1-input mt-3" placeholder="Current password" required value={pw.current_password}
            onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="cp-current" />
          <input type="password" className="p1-input mt-2" placeholder="New password (min 8 chars)" required minLength={8} value={pw.new_password}
            onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="cp-new" />
          <button disabled={busy} className="p1-btn-outline mt-3 !px-4 !py-2 text-xs" data-testid="cp-submit">{busy ? "Updating…" : "Update password"}</button>
          <p className="mt-2 text-xs text-stone-400">Your MFA: <strong>{user?.mfa_enabled ? "Enabled" : "Not enabled — required at next login"}</strong></p>
        </form>

        {showForm && (
          <form onSubmit={invite} className="p1-card p-5 lg:col-span-2" data-testid="invite-staff-form">
            <p className="font-heading font-semibold text-stone-900">Invite a staff member</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input className="p1-input" placeholder="Full name *" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
              <input className="p1-input" type="email" placeholder="Work email *" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
              <input className="p1-input" placeholder="Mobile number * (WhatsApp/SMS ops alerts)" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-phone" />
              <input className="p1-input" placeholder="City (optional — enables city-wise alert routing)" value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="staff-city" />
              <select className="p1-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role">
                {roleOptions().map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
            <button disabled={busy} className="p1-btn-primary mt-3 !px-5 !py-2.5" data-testid="staff-invite-submit">{busy ? "Sending…" : "Send Invite Email"}</button>
            <p className="mt-2 text-xs text-stone-400">They receive an email to set their own password. Privileged roles must set up MFA at first sign-in.</p>
          </form>
        )}
      </div>

      <div className="overflow-x-auto rounded-2xl border border-stone-200 bg-white">
        <table className="w-full min-w-[760px] text-sm" data-testid="staff-table">
          <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
            <tr><th className="p-3">Staff</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3">MFA</th><th className="p-3">Last login</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {staff.map((s) => {
              const self = s.id === user?.id;
              const locked = s.role === "super_admin" || (!isSuper && s.role === "admin");
              return (
                <tr key={s.id} data-testid={`staff-row-${s.id}`}>
                  <td className="p-3"><p className="font-medium text-stone-800">{s.name}{self && <span className="ml-1 text-xs text-stone-400">(you)</span>}</p><p className="text-xs text-stone-400">{s.email}</p>
                    <p className="mt-0.5 text-xs" data-testid={`staff-phone-info-${s.id}`}>
                      {s.phone
                        ? <span className="text-stone-600">{s.phone} · {s.phone_verified
                            ? <span className="font-semibold text-emerald-800">verified</span>
                            : <span className="font-semibold text-amber-700">unverified</span>}
                            {s.notif_eligible && <span className="ml-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-800">alerts eligible</span>}
                          </span>
                        : <span className="font-semibold text-red-600">No mobile — cannot receive WhatsApp/SMS alerts</span>}
                    </p></td>
                  <td className="p-3">
                    {locked ? <span className="text-xs font-semibold text-stone-600">{ROLE_LABELS[s.role]}</span> : (
                      <select className="rounded-lg border border-stone-200 px-2 py-1 text-xs" value={s.role}
                        onChange={(e) => patch(s.id, { role: e.target.value }, "Role updated — sessions revoked")}
                        data-testid={`staff-role-select-${s.id}`}>
                        {roleOptions(s).map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                      </select>
                    )}
                  </td>
                  <td className="p-3"><StatusPill status={s.status === "active" ? "active" : "cancelled"} /><span className="sr-only">{s.status}</span></td>
                  <td className="p-3">{s.mfa_enabled
                    ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-800"><ShieldCheck className="h-3.5 w-3.5" /> On</span>
                    : <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700"><ShieldOff className="h-3.5 w-3.5" /> Pending</span>}</td>
                  <td className="p-3 text-xs text-stone-500">{s.last_login_at ? fmtDay(s.last_login_at) : "Never"}</td>
                  <td className="p-3 text-right">
                    {!locked && !self && (
                      <div className="flex justify-end gap-1.5">
                        {s.status === "active" ? (
                          <button onClick={() => patch(s.id, { status: "disabled" }, "Account disabled — sessions revoked")}
                            className="rounded-full border border-red-200 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50" data-testid={`staff-disable-${s.id}`}>
                            <Ban className="mr-1 inline h-3 w-3" />Disable</button>
                        ) : (
                          <button onClick={() => patch(s.id, { status: "active" }, "Account re-enabled")}
                            className="rounded-full border border-emerald-300 px-3 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-50" data-testid={`staff-enable-${s.id}`}>Enable</button>
                        )}
                        <button onClick={() => revoke(s.id)} className="rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 hover:bg-stone-50" data-testid={`staff-revoke-${s.id}`}>
                          <RefreshCcw className="mr-1 inline h-3 w-3" />Revoke sessions</button>
                        {s.mfa_enabled && (
                          <button onClick={() => mfaReset(s.id)} className="rounded-full border border-stone-200 px-3 py-1 text-xs font-semibold text-stone-600 hover:bg-stone-50" data-testid={`staff-mfa-reset-${s.id}`}>Reset MFA</button>
                        )}
                        {isSuper && (
                          <button onClick={() => setDelTarget(s)} className="rounded-full border border-red-300 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50" data-testid={`staff-delete-${s.id}`}>
                            <Trash2 className="mr-1 inline h-3 w-3" />Delete</button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {delTarget && (
        <ConfirmDelete
          title={`Delete ${delTarget.name}?`}
          body={`Are you sure you want to permanently delete this account (${ROLE_LABELS[delTarget.role] || delTarget.role})? This action cannot be undone.${delTarget.role === "care_manager" ? " Active family assignments will be cleared and upcoming visits cancelled." : ""}`}
          onConfirm={deleteStaff}
          onCancel={() => setDelTarget(null)}
          busy={busy}
          testidPrefix="staff-delete"
        />
      )}
    </div>
  );
}
